import React from 'react';
import axios from 'axios';
import hyperid from 'hyperid';
import { isString, get, isBoolean } from 'lodash';
import queryString from 'query-string';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import { camelCase } from 'change-case';
import md5 from 'md5';
import { processQuestionsAnswers, processQuestionsAnswersNew, checkLogicalAnswer, isAlternative } from './utils';
import { questionTypes } from './shared/GeneralQuestionnaire';
import GeneralQuestionnaireApp from './shared/GeneralQuestionnaireApp';
import './App.css';

const employeeSignInFontSize = {
  fontSize: '1.5rem !important',
};

const { REACT_APP_MY_LOBBY_BACKEND_URL } = process.env;

class EmployeeSignIn extends React.Component {
  constructor(props) {
    super(props);
    const parsedQueryString = queryString.parse(window.location.search);
    this.siteId = parseInt(parsedQueryString.siteId, 10);
    this.employeeId = props.match.params.employeeId;

    sessionStorage.setItem('employeeId', JSON.stringify({ employeeId: this.employeeId }));

    document.querySelector('#my-manifest-placeholder').setAttribute('href', `${process.env.PUBLIC_URL}/.netlify/functions/employee-manifest/${this.employeeId}`);

    this.handleFormSubmit = this.handleFormSubmit.bind(this);

    this.state = {
      filteredQuestions: [],
      initialValues: {},
      isLoading: true,
      userName: '',
      isResultModal: false,
      selectedStage: 0,
      paginationMax: 999,
      site: {},
      employee: {},
    };

    this.answersMap = new Map();
    this.instance = hyperid();
    this.formData = new FormData();
    this.session = {};
    this.site = {};
    this.formData.set('template', undefined);
    this.formData.set('typeId', undefined);
    this.formData.set('siteId', this.siteId);
    this.formData.set('accountId', -1);
    this.ref = React.createRef();
    this.ref.current = { lastPages: [] };
  }

  async componentDidMount() {
    try {
      this.session = await this.initSession();
      if (this.session.error) {
        return this.setState({
          error: this.session.error,
        });
      }

      this.employee = await this.initEmployeeDetails();
      if (this.employee.error) {
        return this.setState({
          error: this.employee.error,
        });
      }

      this.site = await this.initSiteDetails();

      const enabledVisitorTypes = this.site.visitorTypes.filter((v) => v.state === true);

      const [visitorType] = enabledVisitorTypes.filter((v) => v.template === '1' || v.template === 1);

      if (visitorType) {
        let updatedQuestions = [];
        const initialValues = this.state.initialValues;

        if (this.site.isEnableIfThenQuestionnaire && this.site.pagination) {
          const logicalQuestionnaire = (this.site.logicalQuestionnaire || []).find((q) => q.visitorTypes.includes(visitorType.roleId));
          if (logicalQuestionnaire) {
            updatedQuestions = logicalQuestionnaire.logicalQuestions.map((lq) => {
              const q = lq.question;
              const label = isString(get(q, 'label')) ? q.label : get(q, `label["en"]`) || '';
              const hashedLabel = md5(camelCase(label || ''), {
                algorithm: 'md5',
              });
              q.inputName = `question_inputN_${q.uuid || q.inputN}_${hashedLabel}`;
              return { ...lq, question: q };
            });
            this.setState({ site: { ...this.site } });
          } else {
            updatedQuestions = this.getGeneralQuestionnaire(visitorType, this.site);
            this.setState({ site: { ...this.site, isEnableIfThenQuestionnaire: false } });
          }
        } else {
          updatedQuestions = this.getGeneralQuestionnaire(visitorType, this.site);
          this.setState({ site: { ...this.site, isEnableIfThenQuestionnaire: false } });
        }
        updatedQuestions.forEach((q) => {
          if (q.type === questionTypes.TEXT) {
            initialValues[q.inputName] = '';
          }
          if (q.type === questionTypes.RADIOBUTTON) {
            initialValues[q.inputName] = this?.site?.mandatoryAnswersToQuestions === false ? false : null;
          }

          if (q.type === questionTypes.NUMBER) {
            initialValues[q.inputName] = this?.site?.mandatoryAnswersToQuestions === false ? 0 : null;
          }
        });

        this.setState({
          employee: this.employee,
          filteredQuestions: [...updatedQuestions],
          selectedVisitorType: visitorType,
          isLoading: false,
        });

        sessionStorage.setItem('redirectType', JSON.stringify({ redirectType: Number(visitorType?.template) === 1 ? 'employee' : '' }));

        this.setState({
          paginationMax: Math.round(updatedQuestions.length / 2),
        });
      }
    } catch (error) {
      console.warn(error, 'rrr');
    }
    this.props.hideLoader();
  }

  getGeneralQuestionnaire = (visitorType, site) => {
    const questions = site.questions.map((q) => {
      const label = isString(get(q, 'label')) ? q.label : get(q, `label["en"]`) || '';
      const hashedLabel = md5(camelCase(label || ''), {
        algorithm: 'md5',
      });
      q.inputName = `question_inputN_${q.uuid || q.inputN}_${hashedLabel}`;
      return q;
    });
    const qA = questions.filter((q) => q.visitorTypes.includes(visitorType.roleId));
    let qD = [];
    for (var i = 0; i < qA.length; i++) {
      if (qA[i].device && qA[i].device.mobile === true) {
        qD.push(qA[i]);
      }
    }
    if (qD.length > 0) {
      return qD;
    } else {
      return qA;
    }
  };

  async initSession() {
    const id = this.instance.uuid;
    await axios.post(`${REACT_APP_MY_LOBBY_BACKEND_URL}/api/visitor_users`, {
      email: `${id}@my-lobby.com`,
      password: `${id}@my-lobby.com`,
      siteId: this.siteId,
    });

    try {
      const login = await axios.post(`${REACT_APP_MY_LOBBY_BACKEND_URL}/api/visitor_users/login`, {
        email: `${id}@my-lobby.com`,
        password: `${id}@my-lobby.com`,
        siteId: this.siteId,
      });

      const session = login.data;
      return session;
    } catch (error) {
      return {
        error: error.response.data.error,
      };
    }
  }

  async initEmployeeDetails() {
    try {
      const { data } = await axios.get(`${REACT_APP_MY_LOBBY_BACKEND_URL}/api/employees/${this.employeeId}`, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: this.session.id,
        },
      });
      return data.employee;
    } catch (error) {
      return {
        error: error.response.data.error,
      };
    }
  }

  async initSiteDetails() {
    const { data } = await axios.get(`${REACT_APP_MY_LOBBY_BACKEND_URL}/api/sites/${this.siteId}`, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: this.session.id,
      },
    });

    return data.site;
  }

  parseImageStringFromDataUrl = (dataUrl) => {
    const splittedData = dataUrl.split('data:image/png;base64,');
    return splittedData[1];
  };

  handleFormSubmit = async (values) => {
    console.log('====================================');
    console.log('values -->', values);
    console.log('====================================');
    for (const property in values) {
      this.formData.set(property, values[property]);

      if (property.includes('question_inputN_')) {
        this.answersMap.set(property, values[property]);
        this.formData.delete(property);
      }
    }

    this.setState({
      ...this.state,
      userName: this.state.employee.name,
    });
    let responsePageBuilderData = null;
    let isResultModal = false;
    let success = true;
    let isAlt = false;
    if (this.state.filteredQuestions.length > 0) {
      let formattedAnswers = this.state.site.isEnableIfThenQuestionnaire
        ? processQuestionsAnswersNew(this.answersMap, this.state.filteredQuestions)
        : processQuestionsAnswers(this.answersMap, this.state.filteredQuestions);
      formattedAnswers.some((item) => {
        if (this.state.site.isEnableIfThenQuestionnaire) {
          if (checkLogicalAnswer(item, item.question.answer)) {
            isResultModal = true;
            let key = item.question.answer === true ? 'yes' : 'no';
            if (item.default) key = 'no';
            responsePageBuilderData = item[key].data;
            if (isAlternative(item)) {
              isAlt = true;
              responsePageBuilderData = item.question.mobile;
            }
            return true;
          }
        } else {
          if (Number(item.answer) === item.notification && (item.type === 1 || item.type === 3)) {
            isResultModal = true;
            return true;
          }
        }

        return false;
      });
      formattedAnswers = formattedAnswers.map((fa) => {
        return this.state.site.isEnableIfThenQuestionnaire ? fa.question : fa;
      });

      //let check show code or not
      formattedAnswers.some((item) => {
        if (item.type === 3) {
          console.log(item.answer);
        }
        if (Number(item.answer) === item.notification && (item.type === 1 || item.type === 3)) {
          success = false;
          return true;
        }
        return false;
      });

      this.formData.set('answers', JSON.stringify(formattedAnswers));
    }

    this.formData.set('name', this.state.employee.name);
    this.formData.set('company', this.state.employee.company);
    this.formData.set('email', this.state.employee.email);
    this.formData.set('phone', this.state.employee.phoneforsms);
    this.formData.set('template', this.state.selectedVisitorType.template);
    this.formData.set('typeId', this.state.selectedVisitorType.id);
    this.formData.set('accountId', this.state.site.accountId);
    this.formData.set('employeeId', this.employeeId);

    const result = await axios.post(`${REACT_APP_MY_LOBBY_BACKEND_URL}/api/visitor_users/signin?siteId=${this.siteId}`, this.formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: this.session.id,
      },
    });

    if (isAlt) {
      success = true;
    }

    if (result && result.data && result.data.visitorId) {
      sessionStorage.setItem(
        'submitedData',
        JSON.stringify({
          name: this.state.employee.name,
          siteName: this.state.site.name,
          inviteCode: result.data.visitorId.inviteCode,
          success: success,
          isEnableQrCodeWithPinInside: this.state.site.isEnableQrCodeWithPinInside,
          isScanTemperature: this.state.selectedVisitorType.isScanTemperature,
          pageData: responsePageBuilderData,
        }),
      );
      this.setState({
        ...this.state,
        isResultModal: isResultModal,
      });
    }

    if (!this.state.site.isEnableIfThenQuestionnaire) {
      if (!isResultModal && (this.state.selectedVisitorType.isScanTemperature || !this.state.selectedVisitorType.isScanTemperature)) {
        this.props.history.push('/submitted');
      } else if (!isResultModal && !this.state.selectedVisitorType.isScanTemperature) {
        this.props.history.push('/withouttemperature');
      } else if (isResultModal) {
        this.props.history.push('/rejected');
      }
    } else {
      //isEnableIfThenQuestionnaire is enabled
      if (this.state.selectedVisitorType.isScanTemperature) {
        this.props.history.push('/response');
      } else {
        this.props.history.push('/responsewithouttemperature');
      }
    }
  };

  buildValidationSchema = (questions) => {
    let visitorSignInSchema = Yup.object().shape({});

    if (questions.length === 0) {
      return visitorSignInSchema;
    }

    let questionsSchema = [];
    if (!this.state.skippedToLast) {
      questionsSchema = questions.map((lq) => {
        const q = this.state.site.isEnableIfThenQuestionnaire ? lq.question : lq;
        if (q.type === questionTypes.RADIOBUTTON) {
          if (this?.state?.site?.mandatoryAnswersToQuestions || this?.state?.site?.mandatoryAnswersToQuestions === undefined) {
            return [q.inputName, Yup.boolean().required('Please answer the question').nullable()];
          } else {
            return [q.inputName, Yup.boolean().nullable()];
          }
        }
        if (q.type === questionTypes.NUMBER) {
          if (this?.state?.site?.mandatoryAnswersToQuestions || this?.state?.site?.mandatoryAnswersToQuestions === undefined) {
            return [q.inputName, Yup.number().required('Please answer the question').nullable()];
          } else {
            return [q.inputName, Yup.number().nullable()];
          }
        }
        if (q.type === questionTypes.NONRESPONSE) {
          if (this?.state?.site?.mandatoryAnswersToQuestions || this?.state?.site?.mandatoryAnswersToQuestions === undefined) {
            return [Yup.number().notRequired()];
          }
          return [q.inputName, Yup.number().notRequired()];
        }

        if (this?.state?.site?.mandatoryAnswersToQuestions || this?.state?.site?.mandatoryAnswersToQuestions === undefined) {
          return [q.inputName, Yup.string().required('Please answer the question').nullable()];
        } else {
          return [q.inputName, Yup.string().nullable()];
        }
      });
    }

    for (let i = 0; i < questions.length; i++) {
      visitorSignInSchema = Yup.object().shape(Object.fromEntries(questionsSchema));
    }

    return visitorSignInSchema;
  };

  // onResultModalSubmit = () => {
  //   this.setState({
  //     ...this.state,
  //     isResultModal: false,
  //   });

  //   // this.props.history.push('/submitted');
  // };

  setPage(number) {
    const pages = document.querySelectorAll('.questionPart');

    this.setState({ paginationMax: pages.length, selectedStage: number });
    pages.forEach((page, index) => {
      if (this.state.filteredQuestions[number]?.device.mobile === false) {
        number++;
      }
      if (index === number) {
        page.classList.add('active');
      } else {
        page.classList.remove('active');
      }
    });
  }

  onNextClick = () => {
    if (this.state.filteredQuestions.length === 0) {
      this.formik.submitForm();
      return;
    }
    // const selectedPage = this.state.selectedStage + 1;
    const selectedPage = this.state.site.isEnableIfThenQuestionnaire ? this.getNextPageBasedOnAnswer(this.state.selectedStage) : this.state.selectedStage + 1;
    console.log('====================================');
    console.log('selectedPage -->', selectedPage);
    console.log('====================================');
    if (!isBoolean(selectedPage)) this.setPage(selectedPage);
  };

  onPrevClick = () => {
    let selectedPage;
    if (this.state.site.isEnableIfThenQuestionnaire) {
      selectedPage = this.ref.current.lastPages.pop();
      this.setState({ lastPage: null, skippedToLast: false });
    } else {
      selectedPage = this.state.selectedStage - 1;
    }
    this.setPage(selectedPage);
  };

  getNextPageBasedOnAnswer = (currentIndex) => {
    let index = currentIndex;
    let mandatoryQuestionsBeforeQuestionnaire = 1;
    if (currentIndex >= mandatoryQuestionsBeforeQuestionnaire) {
      const { values } = this.formik;
      const questionIndex = currentIndex - mandatoryQuestionsBeforeQuestionnaire;
      const questionWrapper = this.state.filteredQuestions[questionIndex];
      const { question } = questionWrapper;
      if (questionWrapper.default) {
        if (questionWrapper['no'].type === 'QUESTION') {
          console.log('go to next question');
          index = this.getQuestionIndexBasedOnUuid(questionWrapper['no'].data) + mandatoryQuestionsBeforeQuestionnaire - 1;
        } else {
          console.log('go to response page');
          this.setState({ skippedToLast: true, selectedStage: this.state.paginationMax - 1 }, async () => {
            this.submitButton.click();
          });
          index = this.state.paginationMax - 2;
          return false;
        }
      } else {
        const inputKey = question.inputName;
        const response = values[inputKey] || false;
        const responseEnumKey = response ? 'yes' : 'no';

        console.log('====================================');
        console.log('questionWrapper[responseEnumKey].type --->', questionWrapper[responseEnumKey].type);
        console.log('====================================');

        if (questionWrapper[responseEnumKey].type === 'QUESTION') {
          console.log('go to next question');
          index = this.getQuestionIndexBasedOnUuid(questionWrapper[responseEnumKey].data) + mandatoryQuestionsBeforeQuestionnaire - 1;
        } else {
          console.log('go to response page');
          this.setState({ skippedToLast: true, selectedStage: this.state.paginationMax - 1 }, () => {
            this.submitButton.click();
          });
          index = this.state.paginationMax - 2;
          return false;
        }
      }
    }
    this.setState({ lastPage: currentIndex }); //saving lastPage
    this.ref.current.lastPages.push(currentIndex);
    return index + 1;
  };

  getQuestionIndexBasedOnUuid = (uuid) => {
    return this.state.filteredQuestions.findIndex((lq) => lq.uuid === uuid);
  };

  render() {
    const { t } = this.props;
    const { employee, filteredQuestions, initialValues, isLoading, site, error, selectedVisitorType } = this.state;

    if (error) {
      return (
        <div className="container w-full max-w-xs mx-auto">
          <p className="text-center mt-4">{error.message}</p>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="container w-full max-w-xs mx-auto">
          <p className="text-center mt-4">Loading...</p>
        </div>
      );
    }

    const siteLogoUrl = `${REACT_APP_MY_LOBBY_BACKEND_URL}/api/sites/getLogo/${this.siteId}?small=true&access_token=${this.session.id}` || '';
    // console.log('el', {
    //   pagination: site.pagination,
    //   questionsEnabled: this.state.site.questionsEnabled,
    //   selectedStage: this.state.selectedStage,
    //   paginationMax: this.state.paginationMax,
    //   filteredQuestions: filteredQuestions.length,
    // });

    return (
      <div className="container w-full max-w-xs mx-auto">
        <Formik
          initialValues={initialValues}
          validationSchema={() =>
            Yup.lazy((values) => {
              return this.buildValidationSchema(filteredQuestions, values);
            })
          }
          onSubmit={this.handleFormSubmit}
          innerRef={(p) => (this.formik = p)}
        >
          {({ errors, touched, values, handleChange, isSubmitting }) => {
            console.log('====================================');
            console.log('this.state.filteredQuestions -->', this.state.filteredQuestions);
            console.log('====================================');
            if (this?.site?.pagination) {
              return (
                <Form className="bg-white shadow-md px-8 pt-6 pb-8 mb-4">
                  <div id={0} className={'questionPart active'}>
                    <h1 className="text-lg text-black font-bold text-center mt-4">{site.name ? site.name : ''}</h1>
                    <div className="p-2">
                      <img className="mx-auto h-24 object-center object-none" src={siteLogoUrl} alt={site.name ? site.name : ''} />
                    </div>

                    <div className="p-2 text-black text-center pt-8 pb-8">Welcome back {employee.name}, please answer the questionnare before you sign in.</div>
                  </div>

                  {site.questionsEnabled && (
                    <GeneralQuestionnaireApp
                      selectedVisitorType={selectedVisitorType?.id}
                      onChange={handleChange}
                      filteredQuestions={filteredQuestions}
                      errors={errors}
                      touched={touched}
                      values={values}
                      pagination={this?.site?.pagination}
                      fontSize={employeeSignInFontSize}
                      isEnableIfThenQuestionnaire={this.state.site.isEnableIfThenQuestionnaire}
                    />
                  )}
                  {this.state.selectedStage === 0 ? (
                    <div className="flex justify-center">
                      {site.questionsEnabled ? (
                        <div onClick={this.onNextClick} id={'buttonNext'} className={'buttonPagination paginationNext'}>
                          {t('PROCEED')}
                        </div>
                      ) : (
                        <button ref={(p) => (this.submitButton = p)} type="submit" value="Submit" className="buttonPagination paginationNext" disabled={isSubmitting}>
                          {isSubmitting ? 'submitting...' : 'SUBMIT'}
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className={'paginationButtons'}>
                      {this.state.selectedStage > 0 && this.state.selectedStage < this.state.paginationMax && (
                        <div onClick={this.onPrevClick} id={'buttonPrev'} className={'buttonPagination paginationPrev'}>
                          {t('PREV')}
                        </div>
                      )}
                      {this.state.selectedStage === this.state.paginationMax - 1 ? (
                        <button
                          ref={(p) => (this.submitButton = p)}
                          onClick={() => {
                            this.setState({ skippedToLast: true, selectedStage: this.state.paginationMax - 1 }, async () => {
                              this.submitButton.click();
                            });
                          }}
                          type="submit"
                          value="Submit"
                          className="buttonPagination paginationNext"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'subm...' : 'SUBMIT'}
                        </button>
                      ) : (
                        <div onClick={this.onNextClick} id={'buttonNext'} className={'buttonPagination paginationNext'}>
                          {t('NEXT')}
                        </div>
                      )}
                    </div>
                  )}
                </Form>
              );
            }
            return (
              <Form className="bg-blue-gradient shadow-md px-8 pt-6 pb-8 mb-4">
                <h1 className="text-lg text-white  font-bold text-center mt-4">{site.name ? site.name : ''}</h1>
                <div className="p-2">
                  <img
                    className="mx-auto h-24 object-center object-none"
                    src={`${REACT_APP_MY_LOBBY_BACKEND_URL}/api/sites/getLogo/${this.siteId}?small=true&access_token=${this.session.id}`}
                    alt={site.name ? site.name : ''}
                  />
                </div>

                <div className="p-2 text-white text-center pt-8 pb-8">Welcome back {employee.name}, please answer the questionnare before you sign in.</div>

                {site.questionsEnabled && (
                  <GeneralQuestionnaireApp
                    selectedVisitorType={selectedVisitorType?.id}
                    onChange={handleChange}
                    filteredQuestions={filteredQuestions}
                    errors={errors}
                    touched={touched}
                    values={values}
                    fontSize={employeeSignInFontSize}
                    isEnableIfThenQuestionnaire={this.state.site.isEnableIfThenQuestionnaire}
                  />
                )}

                <div className="flex justify-center">
                  <button type="submit" value="Submit" className="buttonPagination paginationNext" disabled={isSubmitting}>
                    {isSubmitting ? 'subm...' : 'SUBMIT'}
                  </button>
                </div>
              </Form>
            );
          }}
        </Formik>
        <p className="text-center text-gray-500 text-xs">&copy;2020 My Lobby. All rights reserved. Employeesignin.</p>
      </div>
    );
  }
}

export default withRouter(withTranslation()(EmployeeSignIn));
